import { BaseService, FormField } from './_base';
import axios from '../axios';
import type { AxiosResponse } from 'axios';


export interface SupportAttachment {
    /** Signed URL, valid for 15 minutes. Re-upload the file after expiry. */
    url: string;
}

export interface SupportConversationRequest {
    /** Required, non-empty message. */
    message: string;
    /** Optional list of up to 10 HTTP(S) URLs returned by uploadAttachment. */
    attachment_urls?: string[];
}

/** The backend forwards the Intercom response without changing its structure. */
export type SupportConversationResponse = Record<string, unknown>;

/** Available on AxiosError<SupportErrorResponse>.response?.data. */
export interface SupportErrorResponse {
    file?: string[];
    message?: string[];
    attachment_urls?: string[] | Record<string, string[]>;
    detail?: string;
}


interface SupportTicket {
    id?: string;
    subject?: string;
    body?: string;
}


class SupportTicketService extends BaseService<SupportTicket> {
    constructor() {
        super("/api/v2/support/tickets/");
        this.formFields = [
            new FormField("subject", "Dein Anliegen", { autofocus: true }),
            new FormField("body", "Detaillierte Beschreibung", {type: 'textarea'}),
        ];
    }

    debug(data: { reference: string, details: any }) {
        return this._post('debug/', data);
    }

    /** Upload one non-empty file smaller than 5,242,880 bytes. */
    uploadAttachment(file: File): Promise<AxiosResponse<SupportAttachment>> {
        const data = new FormData();
        data.append('file', file);

        // Let the browser set the multipart Content-Type, including its boundary.
        return axios.post<SupportAttachment>('/api/v2/support/attachments/', data);
    }

    /** Upload attachments first, then pass their unexpired URLs with the message. */
    createConversation(data: SupportConversationRequest): Promise<AxiosResponse<SupportConversationResponse>> {
        return axios.post<SupportConversationResponse>('/api/v2/support/conversations/', data);
    }
}

const supportTicketService = new SupportTicketService();

export { supportTicketService, SupportTicketService, SupportTicket };

export default supportTicketService;
